(() => {
  'use strict';

  const selector = '.rmcc3';
  const boundAttribute = 'data-rmcc3-bound';
  const roots = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const resetScene = (state) => {
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.frame = 0;
    state.x = 0;
    state.y = 0;
    if (!state.visual) return;
    state.visual.style.setProperty('--rmcc-scene-x', '0px');
    state.visual.style.setProperty('--rmcc-scene-y', '0px');
  };

  const reveal = (root) => {
    root.classList.add('is-seen');
    if (observer) observer.unobserve(root);
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0 })
    : null;

  const syncPreferences = () => {
    roots.forEach((state, root) => {
      root.classList.toggle('is-reduced-motion', reducedMotion.matches);
      if (reducedMotion.matches) {
        // The matching CSS suppresses entrance motion before revealing content.
        reveal(root);
      }
      if (reducedMotion.matches || !finePointer.matches) resetScene(state);
    });
  };

  const bind = (root) => {
    if (root.hasAttribute(boundAttribute)) return;
    root.setAttribute(boundAttribute, '');

    const state = {
      visual: root.querySelector('.rmcc3__visual'),
      frame: 0,
      x: 0,
      y: 0,
      onMove: null,
      onLeave: null
    };
    roots.set(root, state);
    root.classList.toggle('is-reduced-motion', reducedMotion.matches);

    if (reducedMotion.matches || !observer) reveal(root);
    else if (!root.classList.contains('is-seen')) observer.observe(root);

    resetScene(state);
    if (!state.visual) return;

    state.onMove = (event) => {
      if (reducedMotion.matches || !finePointer.matches || event.pointerType === 'touch') return;
      const rect = state.visual.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const unitX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
      const unitY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
      state.x = unitX * 4;
      state.y = unitY * 3;
      if (state.frame) return;

      state.frame = window.requestAnimationFrame(() => {
        state.frame = 0;
        if (reducedMotion.matches || !finePointer.matches) {
          resetScene(state);
          return;
        }
        // Only the scene background consumes these variables, never dog or shadow.
        state.visual.style.setProperty('--rmcc-scene-x', `${state.x.toFixed(2)}px`);
        state.visual.style.setProperty('--rmcc-scene-y', `${state.y.toFixed(2)}px`);
      });
    };
    state.onLeave = () => resetScene(state);
    state.visual.addEventListener('pointermove', state.onMove, { passive: true });
    state.visual.addEventListener('pointerleave', state.onLeave, { passive: true });
    state.visual.addEventListener('pointercancel', state.onLeave, { passive: true });
  };

  const inScope = (scope) => {
    const matches = [];
    if (scope && scope.matches && scope.matches(selector)) matches.push(scope);
    if (scope && scope.querySelectorAll) matches.push(...scope.querySelectorAll(selector));
    return matches;
  };

  const init = (scope) => inScope(scope).forEach(bind);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    inScope(event.target).forEach((root) => {
      const state = roots.get(root);
      if (!state) return;
      if (observer) observer.unobserve(root);
      resetScene(state);
      if (state.visual) {
        state.visual.removeEventListener('pointermove', state.onMove);
        state.visual.removeEventListener('pointerleave', state.onLeave);
        state.visual.removeEventListener('pointercancel', state.onLeave);
      }
      root.removeAttribute(boundAttribute);
      roots.delete(root);
    });
  });

  [reducedMotion, finePointer].forEach((query) => {
    if (query.addEventListener) query.addEventListener('change', syncPreferences);
    else if (query.addListener) query.addListener(syncPreferences);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
